/**
 * Unified validation error store for save-time and custom errors.
 * Supports both GoodsShipment and GoodsItem errors with a single clearing path.
 *
 * Key format:
 *   shipment:{shipmentIndex}:{fieldPath}
 *   item:{shipmentIndex}:{sequence}:{fieldPath}
 */

export interface ValidationError {
  message: string;
  source: 'schema' | 'conflict' | 'custom';
}

type Listener = () => void;

class ValidationErrorStore {
  private errors = new Map<string, ValidationError>();
  private listeners = new Set<Listener>();

  set(key: string, error: ValidationError): void {
    this.errors.set(key, error);
    this.notify();
  }

  clearAll(): void {
    if (this.errors.size === 0) return;
    this.errors.clear();
    this.notify();
  }

  getForItem(
    shipmentIndex: number,
    sequence: number,
  ): Map<string, ValidationError> {
    const prefix = `item:${shipmentIndex}:${sequence}:`;
    const result = new Map<string, ValidationError>();
    for (const [k, v] of this.errors) {
      if (k.startsWith(prefix)) {
        result.set(k.slice(prefix.length), v);
      }
    }
    return result;
  }

  getForShipment(shipmentIndex: number): Map<string, ValidationError> {
    const prefix = `shipment:${shipmentIndex}:`;
    const result = new Map<string, ValidationError>();
    for (const [k, v] of this.errors) {
      if (k.startsWith(prefix)) {
        result.set(k.slice(prefix.length), v);
      }
    }
    return result;
  }

  /* Snapshot for useSyncExternalStore — identity changes on every mutation. */
  getSnapshot = (): Map<string, ValidationError> => this.errors;

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    // Replace the map reference so useSyncExternalStore detects the change
    this.errors = new Map(this.errors);
    for (const l of this.listeners) l();
  }
}

export const validationErrorStore = new ValidationErrorStore();

// Key builders
export const itemErrorKey = (
  shipmentIndex: number,
  sequence: number,
  fieldPath: string,
) => `item:${shipmentIndex}:${sequence}:${fieldPath}`;

export const shipmentErrorKey = (shipmentIndex: number, fieldPath: string) =>
  `shipment:${shipmentIndex}:${fieldPath}`;
